/**
 * Flag Translation Engine
 * Converts raw EHR flag codes into human-readable sentences
 * using patient clinical data (dates, values, imaging, etc.)
 *
 * This is deterministic template-based generation — no AI.
 */

import dayjs from 'dayjs';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Format a relative time string from an ISO date.
 * e.g., "13 months ago", "5+ years ago", "3 weeks ago"
 */
export function relativeTime(isoDate) {
  if (!isoDate) return null;
  const d = dayjs(isoDate);
  if (!d.isValid()) return null;

  const now = dayjs();
  const months = now.diff(d, 'month');

  if (months >= 60) {
    const years = Math.floor(months / 12);
    return `${years}+ years ago`;
  }
  if (months >= 12) {
    return `${months} months ago`;
  }
  if (months >= 1) {
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }

  const weeks = now.diff(d, 'week');
  if (weeks >= 1) {
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }

  const days = now.diff(d, 'day');
  if (days >= 1) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  return 'today';
}

/**
 * Format a date for display: "Oct 16, 2023" or "May 2021"
 */
function displayDate(isoDate) {
  if (!isoDate) return null;
  const d = dayjs(isoDate);
  if (!d.isValid()) return null;
  return d.format('MMM D, YYYY');
}

function displayMonthYear(isoDate) {
  if (!isoDate) return null;
  const d = dayjs(isoDate);
  if (!d.isValid()) return null;
  return d.format('MMM YYYY');
}

/**
 * Parse the Imaging column.
 * Input: "Bone Scan (Last: 2021-05-14), TRUS (Last: 2021-04-20)"
 * Output: [{ type: "bone scan", lastDate: "2021-05-14" }, ...]
 */
export function parseImagingColumn(imagingStr) {
  if (!imagingStr || typeof imagingStr !== 'string') return [];

  const results = [];
  const regex = /([^,(]+?)\s*\(Last:\s*([^)]+)\)/gi;
  let match;

  while ((match = regex.exec(imagingStr)) !== null) {
    const type = match[1].trim();
    const dateStr = match[2].trim();
    const parsed = dayjs(dateStr);
    results.push({
      type,
      lastDate: parsed.isValid() ? parsed.toISOString() : null,
    });
  }

  return results;
}

// ---------------------------------------------------------------------------
// Flag priorities (1 = highest)
// ---------------------------------------------------------------------------

const FLAG_PRIORITY = {
  'scan due to rising psa': 1,
  'provenge candidate': 2,
  'overdue psa': 3,
  'overdue adt': 4,
  'overdue tt': 5,
  'overdue routine scan': 6,
  'overdue appt': 7,
  'no next appointment': 8,
  'not navigated': 9,
};

function getFlagPriority(flagCode) {
  const lower = flagCode.toLowerCase().trim();
  return FLAG_PRIORITY[lower] ?? 99;
}

// ---------------------------------------------------------------------------
// Individual flag translators
// ---------------------------------------------------------------------------

function translateOverduePsa(patient) {
  const date = displayDate(patient.last_psa_date);
  const value = patient.last_psa_value;
  const relative = relativeTime(patient.last_psa_date);
  const rising = patient.last_psa_rises
    ? `. PSA ${patient.last_psa_rises.toLowerCase().includes('yes') || patient.last_psa_rises.toLowerCase().includes('rising') ? 'rising' : patient.last_psa_rises}`
    : '';

  if (!date && !value) {
    return `PSA screening overdue — no recent PSA on file${rising}`;
  }
  if (date && !value) {
    return `PSA screening overdue — last drawn ${date} (${relative}), no value on file${rising}`;
  }
  if (!date && value) {
    return `PSA screening overdue — no date on file (value: ${value})${rising}`;
  }
  return `PSA screening overdue — last drawn ${date} (${relative}, value: ${value})${rising}`;
}

function translateOverdueTt(patient) {
  const date = displayDate(patient.last_tt_date);
  const value = patient.last_tt_value;
  const relative = relativeTime(patient.last_tt_date);

  if (!date && !value) {
    return 'Testosterone check overdue — no recent TT on file';
  }
  if (date && !value) {
    return `Testosterone check overdue — last drawn ${date} (${relative}), no value on file`;
  }
  if (!date && value) {
    return `Testosterone check overdue — no date on file (value: ${value})`;
  }
  return `Testosterone check overdue — last drawn ${date} (${relative}, value: ${value})`;
}

function translateOverdueRoutineScan(patient) {
  const scans = parseImagingColumn(patient.imaging);

  if (scans.length === 0) {
    return 'Routine imaging overdue — no imaging history on file';
  }

  const parts = scans
    .slice(0, 3)
    .map(s => {
      const date = displayMonthYear(s.lastDate);
      return date ? `last ${s.type} ${date}` : `${s.type} (no date)`;
    });

  return `Routine imaging overdue — ${parts.join(', ')}`;
}

function translateNoNextAppointment(patient) {
  const lastDate = displayDate(patient.last_touch);
  const relative = relativeTime(patient.last_touch);

  if (!lastDate) {
    return 'No follow-up scheduled — no recent visit on file';
  }
  return `No follow-up scheduled — last visit was ${lastDate} (${relative})`;
}

function translateOverdueAdt(patient) {
  if (!patient.adt) {
    return 'ADT overdue — no medication on file';
  }
  return `ADT overdue — currently on ${patient.adt}`;
}

function translateScanDueToRisingPsa(patient) {
  const value = patient.last_psa_value;
  const disease = patient.disease_state;

  let text = value
    ? `⚠ Rising PSA (${value}) — scan needed to evaluate progression`
    : '⚠ Rising PSA — scan needed to evaluate progression';

  if (disease) {
    text += ` in ${disease}`;
  }

  return text;
}

function translateProvengeCandidate(patient) {
  const disease = patient.disease_state;
  const value = patient.last_psa_value;
  const rising = patient.last_psa_rises;

  const parts = [];
  if (disease) parts.push(disease);
  if (value) {
    const risingLabel =
      rising && (rising.toLowerCase().includes('yes') || rising.toLowerCase().includes('rising'))
        ? ' (rising)'
        : '';
    parts.push(`PSA ${value}${risingLabel}`);
  }

  if (parts.length === 0) {
    return 'Provenge candidate — review patient eligibility';
  }
  return `Provenge candidate — ${parts.join(', ')}`;
}

function translateNotNavigated() {
  return 'Patient not yet assigned to a navigator';
}

function translateOverdueAppt(patient) {
  const nextDate = displayDate(patient.next_appt);
  if (!nextDate) {
    return 'Appointment overdue — no scheduled date on file';
  }
  return `Appointment overdue — was due ${nextDate}`;
}

// ---------------------------------------------------------------------------
// Main translation dispatcher
// ---------------------------------------------------------------------------

const TRANSLATORS = {
  'overdue psa': translateOverduePsa,
  'overdue tt': translateOverdueTt,
  'overdue routine scan': translateOverdueRoutineScan,
  'no next appointment': translateNoNextAppointment,
  'overdue adt': translateOverdueAdt,
  'scan due to rising psa': translateScanDueToRisingPsa,
  'provenge candidate': translateProvengeCandidate,
  'not navigated': translateNotNavigated,
  'overdue appt': translateOverdueAppt,
};

/**
 * Translate a single flag code into a structured object.
 */
export function translateFlag(flagCode, patient) {
  const lower = flagCode.toLowerCase().trim();
  const translator = TRANSLATORS[lower];

  const display_text = translator
    ? translator(patient)
    : `${flagCode}`;  // Unknown flag — pass through as-is

  return {
    flag_code: flagCode.trim(),
    display_text,
    priority: getFlagPriority(flagCode),
  };
}

/**
 * Translate all flags for a patient.
 * Splits the callouts string, translates each, returns sorted by priority.
 */
export function translateAllFlags(patient) {
  if (!patient.callouts || typeof patient.callouts !== 'string') return [];

  const flags = patient.callouts
    .split(/[,;]/)
    .map(f => f.trim())
    .filter(Boolean);

  return flags
    .map(flag => translateFlag(flag, patient))
    .sort((a, b) => a.priority - b.priority);
}

/**
 * Get the single highest-priority translated flag (for triage list).
 */
export function getPrimaryFlag(translatedFlags) {
  if (!translatedFlags || translatedFlags.length === 0) return null;
  return translatedFlags[0]; // Already sorted by priority
}

// ---------------------------------------------------------------------------
// Suggested Approach Generator
// ---------------------------------------------------------------------------

/**
 * Generate a patient-specific call script from translated flags.
 *
 * Rules:
 * 1. Lead with highest-priority flag (Rising PSA / Provenge first)
 * 2. Group related actions (PSA + TT → "schedule labs")
 * 3. Include appointment context
 * 4. Reference disease state when it changes urgency
 */
export function generateSuggestedApproach(patient, translatedFlags) {
  if (!translatedFlags || translatedFlags.length === 0) return null;

  const flagCodes = new Set(translatedFlags.map(f => f.flag_code.toLowerCase().trim()));
  const parts = [];

  // --- Urgent leads ---
  const hasRisingPsa = flagCodes.has('scan due to rising psa');
  const hasProvenge = flagCodes.has('provenge candidate');

  if (hasRisingPsa) {
    const val = patient.last_psa_value ? ` (${patient.last_psa_value})` : '';
    const disease = patient.disease_state ? ` in ${patient.disease_state} patient` : '';
    parts.push(`Urgent: PSA rising${val}${disease} — imaging scan needed to evaluate progression.`);

    // Add scan context
    const scans = parseImagingColumn(patient.imaging);
    if (scans.length > 0) {
      const mostRecent = scans[0];
      const rel = relativeTime(mostRecent.lastDate);
      if (rel) {
        parts.push(`Last scan was ${displayMonthYear(mostRecent.lastDate)} (${rel}).`);
      }
    }
  }

  if (hasProvenge && !hasRisingPsa) {
    const disease = patient.disease_state || 'disease state unknown';
    const val = patient.last_psa_value ? `, PSA ${patient.last_psa_value}` : '';
    parts.push(`Provenge candidate — ${disease}${val}. Review eligibility and discuss with patient.`);
  }

  // --- Labs grouping ---
  const hasOverduePsa = flagCodes.has('overdue psa');
  const hasOverdueTt = flagCodes.has('overdue tt');

  if (hasOverduePsa && hasOverdueTt) {
    const lastDrawn = patient.last_psa_date
      ? ` (last drawn ${displayMonthYear(patient.last_psa_date)})`
      : '';
    parts.push(`Call to schedule labs — PSA and testosterone both overdue${lastDrawn}.`);
  } else if (hasOverduePsa) {
    parts.push('Call to schedule PSA lab work.');
  } else if (hasOverdueTt) {
    parts.push('Call to schedule testosterone lab work.');
  }

  // --- ADT ---
  if (flagCodes.has('overdue adt')) {
    const med = patient.adt ? `Currently on ${patient.adt}.` : '';
    parts.push(`ADT follow-up overdue. ${med}`.trim());
  }

  // --- Imaging (if not already covered by Rising PSA) ---
  if (flagCodes.has('overdue routine scan') && !hasRisingPsa) {
    const scans = parseImagingColumn(patient.imaging);
    if (scans.length > 0) {
      const scanList = scans.slice(0, 3).map(s => {
        const rel = relativeTime(s.lastDate);
        return rel ? `${s.type} (${rel})` : s.type;
      }).join(', ');
      parts.push(`Imaging overdue — last: ${scanList}.`);
    } else {
      parts.push('Routine imaging overdue.');
    }
  }

  // --- Disease state context ---
  if (patient.disease_state && !hasRisingPsa && !hasProvenge) {
    parts.push(`Patient has ${patient.disease_state}.`);
  }

  // --- Visit context ---
  if (patient.last_touch) {
    const rel = relativeTime(patient.last_touch);
    if (rel && !parts.some(p => p.includes('seen since') || p.includes('last visit'))) {
      const months = dayjs().diff(dayjs(patient.last_touch), 'month');
      if (months >= 6) {
        parts.push(`Hasn't been seen since ${displayMonthYear(patient.last_touch)}.`);
      }
    }
  }

  // --- Appointment context (always last if applicable) ---
  const hasNoAppt = flagCodes.has('no next appointment');
  const hasOverdueAppt = flagCodes.has('overdue appt');

  if (hasNoAppt || hasOverdueAppt) {
    if (!parts.some(p => p.toLowerCase().includes('appointment') || p.toLowerCase().includes('needs to be scheduled'))) {
      parts.push('Needs to be scheduled.');
    }
  }

  // --- Not navigated ---
  if (flagCodes.has('not navigated')) {
    parts.push('Patient needs navigator assignment.');
  }

  if (parts.length === 0) return null;

  return parts.join(' ');
}
